import type { AtmosphereQuality } from "./atmosphere.js";
import { Camera } from "./camera.js";
import { mat4LookAt, mat4Multiply, mat4Orthographic, type Vec3 } from "./math.js";

export const LIGHTING_CONFIG = Object.freeze({
  maximumLocalLights: 8,
  shadowHalfExtent: 32,
  shadowLightDistance: 64,
  shadowFar: 128,
  shadowTextureUnit: 2,
  quality: Object.freeze({
    low: Object.freeze({ shadowSize: 512, localLights: 4, maximumShadowTriangles: 100_000, localShadowLights: 1, localShadowSize: 128, localShadowTriangles: 192_000 }),
    medium: Object.freeze({ shadowSize: 1024, localLights: 6, maximumShadowTriangles: 180_000, localShadowLights: 1, localShadowSize: 192, localShadowTriangles: 288_000 }),
    high: Object.freeze({ shadowSize: 2048, localLights: 8, maximumShadowTriangles: 280_000, localShadowLights: 2, localShadowSize: 256, localShadowTriangles: 384_000 }),
  }),
});

export interface LocalLight {
  readonly id: string;
  readonly type?: "point" | "spot";
  readonly position: readonly [number, number, number];
  readonly color: readonly [number, number, number];
  readonly intensity: number;
  readonly radius: number;
  /** Omit direction for a point light. Spot cone angles are radians. */
  readonly direction?: readonly [number, number, number];
  readonly innerCone?: number;
  readonly outerCone?: number;
  readonly flicker?: boolean;
  /** Bounded attenuation tuning; existing lamps retain 0.20. */
  readonly distanceFalloff?: number;
  /** Focused device radiance uses an exposure shoulder to retain near-surface detail. */
  readonly photometric?: boolean;
}

/** Fixed storage, bounded insertion ranking, and stable IDs break equal-distance ties. */
export class LocalLightSelection {
  readonly positionRadius = new Float32Array(LIGHTING_CONFIG.maximumLocalLights * 4);
  readonly colorIntensity = new Float32Array(LIGHTING_CONFIG.maximumLocalLights * 4);
  readonly directionOuter = new Float32Array(LIGHTING_CONFIG.maximumLocalLights * 4);
  readonly innerCone = new Float32Array(LIGHTING_CONFIG.maximumLocalLights);
  readonly distanceFalloff = new Float32Array(LIGHTING_CONFIG.maximumLocalLights);
  readonly photometric = new Float32Array(LIGHTING_CONFIG.maximumLocalLights);
  readonly selected: LocalLight[] = [];
  private readonly scores = new Float64Array(LIGHTING_CONFIG.maximumLocalLights);

  update(lights: readonly LocalLight[], focus: readonly number[], quality: AtmosphereQuality, timeSeconds: number): void {
    this.selected.length = 0;
    const maximum = LIGHTING_CONFIG.quality[quality].localLights;
    for (const light of lights) {
      if (!validLocalLight(light)) continue;
      const distance = Math.hypot(light.position[0] - focus[0]!, light.position[1] - focus[1]!, light.position[2] - focus[2]!);
      if (distance > light.radius + LIGHTING_CONFIG.shadowHalfExtent) continue;
      const score = light.intensity / (1 + Math.max(0, distance - light.radius * 0.25) ** 2);
      let index = 0;
      while (index < this.selected.length && (this.scores[index]! > score
        || (this.scores[index] === score && this.selected[index]!.id < light.id))) index += 1;
      if (index >= maximum) continue;
      for (let shift = Math.min(this.selected.length, maximum - 1); shift > index; shift -= 1) {
        this.selected[shift] = this.selected[shift - 1]!;
        this.scores[shift] = this.scores[shift - 1]!;
      }
      this.selected[index] = light;
      this.scores[index] = score;
    }
    this.positionRadius.fill(0);
    this.colorIntensity.fill(0);
    this.directionOuter.fill(0);
    this.innerCone.fill(0);
    this.distanceFalloff.fill(0.2);
    this.photometric.fill(0);
    for (let index = 0; index < this.selected.length; index += 1) {
      const light = this.selected[index]!;
      const offset = index * 4;
      this.distanceFalloff[index] = light.distanceFalloff ?? 0.2;
      this.photometric[index] = light.photometric ? 1 : 0;
      this.positionRadius.set(light.position, offset);
      this.positionRadius[offset + 3] = light.radius;
      this.colorIntensity.set(light.color, offset);
      this.colorIntensity[offset + 3] = light.intensity * (light.flicker ? lightFlicker(timeSeconds) : 1);
      if (light.direction) {
        const inverseLength = 1 / Math.hypot(...light.direction);
        this.directionOuter[offset] = light.direction[0] * inverseLength;
        this.directionOuter[offset + 1] = light.direction[1] * inverseLength;
        this.directionOuter[offset + 2] = light.direction[2] * inverseLength;
        const outer = Math.max(0.02, Math.min(Math.PI / 2, light.outerCone ?? 0.8));
        this.directionOuter[offset + 3] = Math.cos(outer);
        this.innerCone[index] = Math.cos(Math.max(0, Math.min(outer - 0.01, light.innerCone ?? outer * 0.6)));
      } else this.directionOuter[offset + 3] = -1;
    }
  }
}

export function validLocalLight(light: LocalLight): boolean {
  return light.id.length > 0 && light.radius > 0 && light.radius <= 64 && light.intensity > 0 && light.intensity <= (light.photometric ? 256 : 16)
    && Number.isFinite(light.radius + light.intensity) && light.position.every(Number.isFinite)
    && light.color.every((value) => Number.isFinite(value) && value >= 0 && value <= 1)
    && (!light.direction || (light.direction.every(Number.isFinite) && Math.hypot(...light.direction) > 0.001))
    && (light.distanceFalloff === undefined || (Number.isFinite(light.distanceFalloff) && light.distanceFalloff >= 0.01 && light.distanceFalloff <= 1))
    && (light.innerCone === undefined || Number.isFinite(light.innerCone))
    && (light.outerCone === undefined || Number.isFinite(light.outerCone));
}

/** Slow deterministic damage modulation; never blanks the lamp or strobes. */
export function lightFlicker(timeSeconds: number): number {
  return 0.965 + 0.022 * Math.sin(timeSeconds * 1.7) + 0.013 * Math.sin(timeSeconds * 3.1 + 0.7);
}

/** LOD distances remain player-relative while visibility uses the light volume. */
export class DirectionalShadowCamera extends Camera {
  updateFocus(focus: readonly [number, number, number], resolution: number, sun: readonly [number, number, number] = [-0.48, 0.78, 0.39]): void {
    const length = Math.hypot(...sun);
    const direction: Vec3 = [sun[0] / length, sun[1] / length, sun[2] / length];
    const up: Vec3 = Math.abs(direction[1]) > 0.99 ? [0, 0, 1] : [0, 1, 0];
    const rotation = mat4LookAt(direction, [0, 0, 0], up);
    const texel = LIGHTING_CONFIG.shadowHalfExtent * 2 / resolution;
    const lightX = Math.round((rotation[0]! * focus[0] + rotation[4]! * focus[1] + rotation[8]! * focus[2]) / texel) * texel;
    const lightY = Math.round((rotation[1]! * focus[0] + rotation[5]! * focus[1] + rotation[9]! * focus[2]) / texel) * texel;
    const lightZ = rotation[2]! * focus[0] + rotation[6]! * focus[1] + rotation[10]! * focus[2];
    const center: Vec3 = [
      rotation[0]! * lightX + rotation[1]! * lightY + rotation[2]! * lightZ,
      rotation[4]! * lightX + rotation[5]! * lightY + rotation[6]! * lightZ,
      rotation[8]! * lightX + rotation[9]! * lightY + rotation[10]! * lightZ,
    ];
    const eye: Vec3 = [
      center[0] + direction[0] * LIGHTING_CONFIG.shadowLightDistance,
      center[1] + direction[1] * LIGHTING_CONFIG.shadowLightDistance,
      center[2] + direction[2] * LIGHTING_CONFIG.shadowLightDistance,
    ];
    this.position[0] = focus[0]; this.position[1] = focus[1]; this.position[2] = focus[2];
    this.target[0] = focus[0]; this.target[1] = focus[1]; this.target[2] = focus[2];
    this.view = mat4LookAt(eye, center, up);
    const half = LIGHTING_CONFIG.shadowHalfExtent;
    this.projection = mat4Orthographic(-half, half, -half, half, 0.1, LIGHTING_CONFIG.shadowFar);
    this.viewProjection = mat4Multiply(this.projection, this.view);
  }

  override isSphereVisible(x: number, y: number, z: number, radius: number): boolean {
    const matrix = this.viewProjection;
    for (let row = 0; row < 3; row += 1) {
      const value = matrix[row]! * x + matrix[4 + row]! * y + matrix[8 + row]! * z + matrix[12 + row]!;
      const padding = radius * Math.hypot(matrix[row]!, matrix[4 + row]!, matrix[8 + row]!);
      if (Math.abs(value) > 1 + padding) return false;
    }
    return true;
  }
}

export const LIGHTING_GLSL = `
uniform mat4 uShadowMatrix;
uniform highp sampler2D uShadowMap;
uniform float uShadowTexel;
uniform float uShadowEnabled;
uniform int uLightCount;
uniform vec4 uLightPositionRadius[${LIGHTING_CONFIG.maximumLocalLights}];
uniform vec4 uLightColorIntensity[${LIGHTING_CONFIG.maximumLocalLights}];
uniform vec4 uLightDirectionOuter[${LIGHTING_CONFIG.maximumLocalLights}];
uniform float uLightInnerCone[${LIGHTING_CONFIG.maximumLocalLights}];
uniform float uLightDistanceFalloff[${LIGHTING_CONFIG.maximumLocalLights}];
uniform float uLightPhotometric[${LIGHTING_CONFIG.maximumLocalLights}];
uniform highp sampler2D uLocalStatic0;
uniform highp sampler2D uLocalDynamic0;
uniform highp sampler2D uLocalStatic1;
uniform highp sampler2D uLocalDynamic1;
uniform mat4 uLocalShadowMatrices[12];
uniform vec4 uLocalShadowInfo[2]; // selected light index, strength, face texel, unused

float localLightVisibility(int lightIndex, vec3 world, vec3 normal) {
  int slot = -1;
  if (uLocalShadowInfo[0].y > 0.0 && int(uLocalShadowInfo[0].x) == lightIndex) slot = 0;
  if (uLocalShadowInfo[1].y > 0.0 && int(uLocalShadowInfo[1].x) == lightIndex) slot = 1;
  if (slot < 0) return 1.0;
  vec3 delta = world - uLightPositionRadius[lightIndex].xyz;
  vec3 absolute = abs(delta);
  int face;
  if (absolute.x >= absolute.y && absolute.x >= absolute.z) face = delta.x >= 0.0 ? 0 : 1;
  else if (absolute.y >= absolute.z) face = delta.y >= 0.0 ? 2 : 3;
  else face = delta.z >= 0.0 ? 4 : 5;
  // Offset the receiver along its normal in world metres, not light-space Z.
  // This avoids both self acne and a distance-dependent detached silhouette.
  vec3 toLight = normalize(-delta);
  float texel = uLocalShadowInfo[slot].z;
  // Grazing torch rays cover increasingly large ground footprints per texel.
  // Scale the normal offset with that footprint to avoid dark horizontal bands.
  float receiverBias = min(0.20, max(0.025, length(delta) * texel * 1.25));
  vec3 receiver = world + normal * (0.008 + receiverBias * (1.0 - max(0.0, dot(normal, toLight))));
  vec4 clip = uLocalShadowMatrices[slot * 6 + face] * vec4(receiver, 1.0);
  vec3 coordinate = clip.xyz / clip.w * 0.5 + 0.5;
  if (coordinate.z < 0.0 || coordinate.z > 1.0) return 1.0;
  vec2 cell = vec2(float(face % 3), float(face / 3));
  float visible = 0.0;
  for (int y = 0; y < 2; y++) for (int x = 0; x < 2; x++) {
    vec2 uv = (cell + clamp(coordinate.xy + (vec2(float(x), float(y)) - 0.5) * texel, vec2(texel * 0.5), vec2(1.0 - texel * 0.5))) / vec2(3.0, 2.0);
    float depth;
    if (slot == 0) depth = min(texture(uLocalStatic0, uv).r, texture(uLocalDynamic0, uv).r);
    else depth = min(texture(uLocalStatic1, uv).r, texture(uLocalDynamic1, uv).r);
    visible += step(coordinate.z - 0.000015, depth);
  }
  return mix(1.0, visible * 0.25, uLocalShadowInfo[slot].y);
}

float directionalVisibility(vec3 world, vec3 normal) {
  if (uShadowEnabled <= 0.0) return 1.0;
  vec3 coordinate = (uShadowMatrix * vec4(world, 1.0)).xyz * 0.5 + 0.5;
  if (any(lessThan(coordinate, vec3(0.0))) || any(greaterThan(coordinate, vec3(1.0)))) return 1.0;
  float bias = max(0.00032, 0.0011 * (1.0 - max(0.0, dot(normal, normalize(uSunDirection)))));
  float visibility = 0.0;
  for (int y = 0; y < 2; y++) for (int x = 0; x < 2; x++) {
    vec2 offset = (vec2(float(x), float(y)) - 0.5) * uShadowTexel;
    visibility += step(coordinate.z - bias, texture(uShadowMap, coordinate.xy + offset).r);
  }
  float edge = max(abs(coordinate.x - 0.5), abs(coordinate.y - 0.5)) * 2.0;
  return mix(1.0, mix(visibility * 0.25, 1.0, smoothstep(0.80, 0.99, edge)), uShadowEnabled);
}

vec3 worldLighting(vec3 world, vec3 normal, float lightBand) {
  vec3 groundAmbient = uAmbientColor * vec3(0.54, 0.48, 0.42);
  vec3 hemisphere = mix(groundAmbient, uAmbientColor, clamp(normal.y * 0.5 + 0.5, 0.0, 1.0));
  vec3 lighting = hemisphere + uSunColor * lightBand * 0.78 * directionalVisibility(world, normal);
  for (int index = 0; index < ${LIGHTING_CONFIG.maximumLocalLights}; index++) {
    if (index >= uLightCount) break;
    vec3 toLight = uLightPositionRadius[index].xyz - world;
    float squaredDistance = dot(toLight, toLight);
    float radius = uLightPositionRadius[index].w;
    if (squaredDistance >= radius * radius) continue;
    float range = max(0.0, 1.0 - squaredDistance / (radius * radius));
    vec3 direction = toLight * inversesqrt(max(squaredDistance, 0.0001));
    float cone = 1.0;
    if (uLightDirectionOuter[index].w > -0.5) {
      cone = smoothstep(uLightDirectionOuter[index].w, uLightInnerCone[index], dot(-direction, uLightDirectionOuter[index].xyz));
    }
    if (cone <= 0.0) continue;
    bool photometric = uLightPhotometric[index] > 0.5;
    float attenuation = (photometric ? range : range * range) / (1.0 + squaredDistance * uLightDistanceFalloff[index]);
    vec3 radiance = uLightColorIntensity[index].rgb * uLightColorIntensity[index].w * attenuation * cone * max(0.0, dot(normal, direction));
    // The established linear/quantized palette otherwise crushes a torch's
    // grazing-angle ground illumination. A bounded camera-response shoulder
    // preserves throw without washing nearby materials into a white disc.
    if (photometric) radiance = radiance / (vec3(1.0) + radiance * 0.5);
    lighting += radiance * localLightVisibility(index, world, normal);
  }
  return lighting;
}`;

export interface LightingUniforms {
  readonly shadowMatrix: WebGLUniformLocation | null;
  readonly shadowMap: WebGLUniformLocation | null;
  readonly shadowTexel: WebGLUniformLocation | null;
  readonly shadowEnabled: WebGLUniformLocation | null;
  readonly lightCount: WebGLUniformLocation | null;
  readonly positionRadius: WebGLUniformLocation | null;
  readonly colorIntensity: WebGLUniformLocation | null;
  readonly directionOuter: WebGLUniformLocation | null;
  readonly innerCone: WebGLUniformLocation | null;
  readonly distanceFalloff: WebGLUniformLocation | null;
  readonly photometric: WebGLUniformLocation | null;
  readonly ambientColor: WebGLUniformLocation | null;
  readonly sunDirection: WebGLUniformLocation | null;
  readonly sunColor: WebGLUniformLocation | null;
  readonly localSamplers: readonly (WebGLUniformLocation | null)[];
  readonly localMatrices: WebGLUniformLocation | null;
  readonly localInfo: WebGLUniformLocation | null;
}

export function createLightingUniforms(gl: WebGL2RenderingContext, program: WebGLProgram): LightingUniforms {
  return {
    shadowMatrix: gl.getUniformLocation(program, "uShadowMatrix"),
    shadowMap: gl.getUniformLocation(program, "uShadowMap"),
    shadowTexel: gl.getUniformLocation(program, "uShadowTexel"),
    shadowEnabled: gl.getUniformLocation(program, "uShadowEnabled"),
    lightCount: gl.getUniformLocation(program, "uLightCount"),
    positionRadius: gl.getUniformLocation(program, "uLightPositionRadius[0]"),
    colorIntensity: gl.getUniformLocation(program, "uLightColorIntensity[0]"),
    directionOuter: gl.getUniformLocation(program, "uLightDirectionOuter[0]"),
    innerCone: gl.getUniformLocation(program, "uLightInnerCone[0]"),
    distanceFalloff: gl.getUniformLocation(program, "uLightDistanceFalloff[0]"),
    photometric: gl.getUniformLocation(program, "uLightPhotometric[0]"),
    ambientColor: gl.getUniformLocation(program, "uAmbientColor"),
    sunDirection: gl.getUniformLocation(program, "uSunDirection"),
    sunColor: gl.getUniformLocation(program, "uSunColor"),
    localSamplers: ["uLocalStatic0", "uLocalDynamic0", "uLocalStatic1", "uLocalDynamic1"].map((name) => gl.getUniformLocation(program, name)),
    localMatrices: gl.getUniformLocation(program, "uLocalShadowMatrices[0]"),
    localInfo: gl.getUniformLocation(program, "uLocalShadowInfo[0]"),
  };
}
